import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';

interface UsageStatus {
    has_subscription: boolean;
    current_usage: number;
    limit: number;
    remaining: number;
    percentage_used: number;
    at_risk: boolean;
    exceeded: boolean;
    unique_candidates?: string[]; // List of unique candidate emails
    plan_name?: string;
    plan_price?: number;
    plan_interval?: string;
}

export const useUsageStatus = (refreshTrigger?: any) => {
    const { isAdmin } = useUser();
    const [usage, setUsage] = useState<UsageStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchUsageStatus = useCallback(async () => {
        // Only fetch usage status for admin users
        if (!isAdmin()) {
            setLoading(false);
            setUsage(null);
            setError(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await api.get('usage/status/');
            setUsage(response.data);
        } catch (err: any) {
            console.error('Error fetching usage status:', err);
            setError(err.response?.data?.error || 'Failed to load usage status');
            setUsage(null);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchUsageStatus();
    }, [fetchUsageStatus, refreshTrigger]);

    const refresh = useCallback(() => {
        fetchUsageStatus();
    }, [fetchUsageStatus]);

    return {
        usage,
        loading,
        error,
        refresh
    };
};